import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Fonts from "../../ui/global/Fonts";
import Colors from "../../ui/global/Colors";
import { Auth } from '@supabase/auth-ui-react'
import { ThemeSupa } from '@supabase/auth-ui-shared'
import { SupabaseClient } from '@supabase/supabase-js'
import { useLocation, useNavigate } from 'react-router-dom'
import LegalFooter from "../legal/LegalFooter";
import Logo from "../../ui/components/Logo";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100svh;
  width: 100%;
  background-color: ${Colors.black};
  padding: 10px;
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 40px auto;
  padding: 20px;
  background-color: ${Colors.white};
  border-radius: 8px;
  border: 1px solid ${Colors.grey1};
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: 16px;
`;

const Header = styled.h4`
  color: ${Colors.black};
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 24px;
  text-align: center;
  margin: 10px 0 20px 0;
`;

const ErrorMessage = styled.p`
  color: ${Colors.red};
  margin-bottom: 20px;
  font-size: 18px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  gap: 0;
`;

const FooterText = styled.p`
  color: ${Colors.grey6};
  font-family: ui-sans-serif, sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 4px 0;
`;

const FooterLink = styled.a`
  color: ${Colors.grey6};
  font-family: ui-sans-serif, sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 4px 0;
  text-decoration: underline;
  cursor: pointer;

  &:hover, &:active {
    color: ${Colors.grey8};
  }
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  border: 1px solid ${Colors.grey2};
  border-radius: 4px;
  background-color: ${Colors.white};
  transition: border-color 0.2s;

  &:focus-within {
    border-color: ${Colors.grey9};
  }
`;

const AtSymbol = styled.span`
  padding: 10px 0 10px 15px;
  color: ${Colors.black};
  font-family: ui-sans-serif, sans-serif;
  font-size: 14px;
  user-select: none;
`;

const StyledInput = styled.input`
  flex: 1;
  padding: 10px 15px 10px 0;
  border: none;
  font-family: ui-sans-serif, sans-serif;
  font-size: 14px;
  color: ${Colors.black};
  background-color: transparent;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${Colors.grey4};
  }
`;

const StyledLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #808080;
  font-family: ui-sans-serif, sans-serif;
  font-size: 14px;
`;

interface SignUpProps {
  supabase: SupabaseClient
}

const SignUp = ({ supabase }: SignUpProps) => {
  const [error, setError] = useState<string | null>(null);
  const [tag, setTag] = useState(`user_${Math.random().toString(36).substring(2, 6)}`.replace('@', ''));
  const location = useLocation();
  const navigate = useNavigate();

  const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTag(e.target.value);
  };

  // Store the redirect path in localStorage if present
  useEffect(() => {
    const state = location.state as { from?: Location };
    const params = new URLSearchParams(location.search);
    const fromParam = params.get('from');

    // console.log('fromParam', fromParam);
    // console.log('state', state);

    if (fromParam) {
      // console.log('Storing redirect path in useEffect:', fromParam);
      localStorage.setItem('redirectPath', fromParam);
    } else if (state?.from && !localStorage.getItem('redirectPath')) { // Only set if not already set
      // console.log('Storing redirect path in useEffect:', state.from.pathname);
      localStorage.setItem('redirectPath', state.from.pathname);
    }
  }, [location.state, location.search]);

  return (
    <PageWrapper id="signup-page">
      <Logo colorMode="dark" size={36}/> {/* Add Logo component here */}
      <Container>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Header>Create Account</Header>
        {/* <StyledLabel className="supabase-auth-ui_ui-label c-bpexlo">User tag</StyledLabel>
        <InputContainer>
          <AtSymbol>@</AtSymbol>
          <StyledInput
            type="text"
            value={tag}
            onChange={handleTagChange}
            placeholder="username"
            className="supabase-auth-ui_ui-input c-dEnagJ c-dEnagJ-bBzSYw-type-default"
          />
        </InputContainer> */}
        <Auth
          supabaseClient={supabase}
          view="sign_up"
          providers={[ 'google' ]} /* 'google', 'discord', 'github', */
          theme="default"
          redirectTo={window.location.origin + '/login'}
          showLinks={false}
          socialLayout="vertical"
          appearance={{
            theme: ThemeSupa,
            variables: {
              default: {
                colors: {
                  brand: Colors.grey9,
                  brandAccent: Colors.grey8,
                  // Social button styling
                  defaultButtonBackground: Colors.background,
                  defaultButtonBorder: Colors.grey1,
                  defaultButtonText: Colors.black,
                },
              },
            },
            style: {
              divider: {
                // display: 'none',
              },
            },
          }}
          localization={{
            variables: {
              sign_up: {
                social_provider_text: "Continue with {{provider}}",
                email_label: 'Email address',
                password_label: 'Password',
                button_label: 'Sign up',
                loading_button_label: 'Signing up...',
                email_input_placeholder: 'Your email address',
                password_input_placeholder: 'Your password',
                link_text: "Already have an account? Log in",
              }
            }
          }}
        />
        <Footer>
          <FooterText>Already have an account? <FooterLink href="/login">Log in</FooterLink></FooterText>
        </Footer>
      </Container>
      <LegalFooter />
    </PageWrapper>
  );
};

export default SignUp;